// ----------------------------------------------------------------------------
// VideoPlayerModal — self-contained modal video player with transport controls.
// Inspired by framestack's video player UI.
// Usage: openVideoPlayer({ url, blob, name, downloadable, downloadUrl });
// ----------------------------------------------------------------------------

let _modal = null;
let _video = null;
let _controls = {};
let _isDragging = false;
let _cleanupFns = [];

function _formatTime(seconds) {
   if (isNaN(seconds)) return '0:00';
   const mins = Math.floor(seconds / 60);
   const secs = Math.floor(seconds % 60);
   return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function _ensureModal() {
   if (_modal) return _modal;

   _modal = document.createElement('div');
   _modal.className = 'vpm-overlay';
   _modal.innerHTML = `
      <div class="vpm-container">
         <div class="vpm-header">
            <span class="vpm-title"></span>
            <button class="vpm-close" title="Close (Esc)">✕</button>
         </div>
         <div class="vpm-video-wrap">
            <video class="vpm-video"></video>
         </div>
         <div class="vpm-controls">
            <div class="vpm-seek-bar">
               <div class="vpm-seek-fill"></div>
               <div class="vpm-seek-thumb"></div>
            </div>
            <div class="vpm-transport">
               <button class="vpm-btn vpm-play" title="Play / Pause">▶</button>
               <button class="vpm-btn vpm-stop" title="Stop">■</button>
               <div class="vpm-time">
                  <span class="vpm-cur">0:00</span>
                  <span class="vpm-sep">/</span>
                  <span class="vpm-dur">0:00</span>
               </div>
               <select class="vpm-speed">
                  <option value="0.25">0.25×</option>
                  <option value="0.5">0.5×</option>
                  <option value="0.75">0.75×</option>
                  <option value="1" selected>1×</option>
                  <option value="1.25">1.25×</option>
                  <option value="1.5">1.5×</option>
                  <option value="2">2×</option>
               </select>
               <div class="vpm-spacer"></div>
               <button class="vpm-btn vpm-download" title="Download" style="display:none;">⬇</button>
            </div>
         </div>
      </div>
   `;
   document.body.appendChild(_modal);

   _video = _modal.querySelector('.vpm-video');
   _controls = {
      seekBar: _modal.querySelector('.vpm-seek-bar'),
      seekFill: _modal.querySelector('.vpm-seek-fill'),
      seekThumb: _modal.querySelector('.vpm-seek-thumb'),
      playBtn: _modal.querySelector('.vpm-play'),
      stopBtn: _modal.querySelector('.vpm-stop'),
      curTime: _modal.querySelector('.vpm-cur'),
      durTime: _modal.querySelector('.vpm-dur'),
      speed: _modal.querySelector('.vpm-speed'),
      download: _modal.querySelector('.vpm-download'),
      close: _modal.querySelector('.vpm-close'),
      title: _modal.querySelector('.vpm-title'),
   };

   // Inject styles once
   if (!document.getElementById('vpm-styles')) {
      const style = document.createElement('style');
      style.id = 'vpm-styles';
      style.textContent = `
.vpm-overlay {
   position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
   background: rgba(0,0,0,0.85); z-index: 100000;
   display: none; align-items: center; justify-content: center;
   font-family: monospace;
}
.vpm-overlay.visible { display: flex; }
.vpm-container {
   background: var(--c1, #1a1a1a); border: 1px solid var(--c4, #444);
   border-radius: 4px; max-width: 90vw; max-height: 90vh;
   display: flex; flex-direction: column; overflow: hidden;
   box-shadow: 0 8px 32px rgba(0,0,0,0.6);
}
.vpm-header {
   display: flex; align-items: center; justify-content: space-between;
   padding: 6px 10px; background: var(--c2, #222); border-bottom: 1px solid var(--c4, #444);
   flex-shrink: 0;
}
.vpm-title {
   color: var(--cf, #ccc); font-size: 11px; font-weight: bold;
   overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
   max-width: 400px;
}
.vpm-close {
   background: none; border: none; color: var(--cf, #ccc);
   font-size: 14px; cursor: pointer; padding: 2px 6px; border-radius: 3px;
}
.vpm-close:hover { background: var(--c4, #444); color: #fff; }
.vpm-video-wrap {
   background: #000; display: flex; align-items: center; justify-content: center;
   flex-shrink: 1; min-height: 100px; overflow: hidden;
}
.vpm-video {
   max-width: 80vw; max-height: 70vh; display: block;
}
.vpm-controls {
   background: var(--c2, #222); border-top: 1px solid var(--c4, #444);
   flex-shrink: 0;
}
.vpm-seek-bar {
   height: 8px; background: var(--c4, #333); cursor: pointer; position: relative;
}
.vpm-seek-fill {
   height: 100%; background: var(--cf, #aaa); width: 0%; pointer-events: none;
   transition: width 0.05s linear;
}
.vpm-seek-bar:hover .vpm-seek-fill { background: rgba(255,255,255,0.9); }
.vpm-seek-thumb {
   position: absolute; top: 50%; width: 10px; height: 10px;
   background: var(--cf, #aaa); border-radius: 50%; transform: translate(-50%, -50%);
   pointer-events: none; opacity: 0; transition: opacity 0.15s;
}
.vpm-seek-bar:hover .vpm-seek-thumb { opacity: 1; }
.vpm-transport {
   display: flex; align-items: center; gap: 8px; padding: 8px 10px;
}
.vpm-btn {
   width: 30px; height: 26px; border: 1px solid var(--c4, #444);
   background: var(--c1, #1a1a1a); color: var(--cf, #ccc);
   font-size: 11px; cursor: pointer; display: flex; align-items: center;
   justify-content: center; border-radius: 3px;
}
.vpm-btn:hover { background: var(--c4, #444); }
.vpm-btn.playing { background: var(--cf, #aaa); color: var(--c1, #1a1a1a); border-color: var(--cf, #aaa); }
.vpm-time {
   font-size: 10px; color: var(--cf, #ccc); display: flex; gap: 4px; align-items: center;
}
.vpm-time .vpm-sep { opacity: 0.4; }
.vpm-speed {
   height: 24px; padding: 0 4px; background: var(--c1, #1a1a1a);
   border: 1px solid var(--c4, #444); color: var(--cf, #ccc);
   font-size: 10px; font-family: monospace; cursor: pointer; outline: none; border-radius: 3px;
}
.vpm-speed:hover { border-color: var(--cf, #ccc); }
.vpm-speed option { background: var(--c1, #1a1a1a); color: var(--cf, #ccc); }
.vpm-spacer { flex: 1; }
`;
      document.head.appendChild(style);
   }

   // Event handlers
   _controls.playBtn.addEventListener('click', () => {
      if (_video.paused) {
         _video.play().catch(() => {});
         _controls.playBtn.textContent = '⏸';
         _controls.playBtn.classList.add('playing');
      } else {
         _video.pause();
         _controls.playBtn.textContent = '▶';
         _controls.playBtn.classList.remove('playing');
      }
   });

   _controls.stopBtn.addEventListener('click', () => {
      _video.pause();
      _video.currentTime = 0;
      _controls.playBtn.textContent = '▶';
      _controls.playBtn.classList.remove('playing');
      _controls.seekFill.style.width = '0%';
      _controls.curTime.textContent = '0:00';
   });

   _controls.seekBar.addEventListener('click', (e) => {
      if (!_video.duration) return;
      const rect = _controls.seekBar.getBoundingClientRect();
      _video.currentTime = ((e.clientX - rect.left) / rect.width) * _video.duration;
   });

   _controls.seekBar.addEventListener('mousedown', (e) => {
      _isDragging = true;
      _seekTo(e);
   });

   _controls.speed.addEventListener('change', () => {
      _video.playbackRate = parseFloat(_controls.speed.value);
   });

   _video.addEventListener('timeupdate', () => {
      if (!_video.duration) return;
      const pct = (_video.currentTime / _video.duration) * 100;
      _controls.seekFill.style.width = pct + '%';
      _controls.seekThumb.style.left = pct + '%';
      _controls.curTime.textContent = _formatTime(_video.currentTime);
   });

   _video.addEventListener('loadedmetadata', () => {
      _controls.durTime.textContent = _formatTime(_video.duration);
   });

   _video.addEventListener('ended', () => {
      _controls.playBtn.textContent = '▶';
      _controls.playBtn.classList.remove('playing');
      _controls.seekFill.style.width = '100%';
   });

   _controls.close.addEventListener('click', closeVideoPlayer);

   _modal.addEventListener('click', (e) => {
      if (e.target === _modal) closeVideoPlayer();
   });

   return _modal;
}

function _seekTo(e) {
   if (!_video.duration) return;
   const rect = _controls.seekBar.getBoundingClientRect();
   const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
   _video.currentTime = ratio * _video.duration;
}

/**
 * Open the video player modal.
 * @param {Object} opts
 * @param {string} [opts.url] - Object URL or remote URL to play
 * @param {Blob} [opts.blob] - Blob to create URL from
 * @param {string} [opts.name] - Display name / title
 * @param {boolean} [opts.downloadable=false] - Show download button
 * @param {string} [opts.downloadUrl] - URL for download (defaults to play url)
 * @param {string} [opts.downloadName] - Filename for download
 */
function openVideoPlayer(opts = {}) {
   _ensureModal();

   // Cleanup previous
   _cleanup();
   _video.pause();
   _video.removeAttribute('src');
   _video.load();

   let url = opts.url;
   let createdUrl = false;
   if (!url && opts.blob) {
      url = URL.createObjectURL(opts.blob);
      createdUrl = true;
   }
   if (!url) return;

   _video.src = url;
   _controls.title.textContent = opts.name || '';
   _controls.playBtn.textContent = '▶';
   _controls.playBtn.classList.remove('playing');
   _controls.seekFill.style.width = '0%';
   _controls.curTime.textContent = '0:00';
   _controls.durTime.textContent = '0:00';
   _controls.speed.value = '1';

   // Download button
   if (opts.downloadable) {
      _controls.download.style.display = '';
      _controls.download.onclick = () => {
         const a = document.createElement('a');
         a.href = opts.downloadUrl || url;
         a.download = opts.downloadName || opts.name || 'video';
         a.click();
      };
   } else {
      _controls.download.style.display = 'none';
   }

   _modal.classList.add('visible');

   // Auto-play
   _video.play().catch(() => {});

   // Cleanup handlers
   const escHandler = (ev) => { if (ev.key === 'Escape') closeVideoPlayer(); };
   document.addEventListener('keydown', escHandler);
   _cleanupFns.push(() => document.removeEventListener('keydown', escHandler));

   const mouseMoveHandler = (e) => { if (_isDragging) _seekTo(e); };
   const mouseUpHandler = () => { _isDragging = false; };
   document.addEventListener('mousemove', mouseMoveHandler);
   document.addEventListener('mouseup', mouseUpHandler);
   _cleanupFns.push(() => {
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
   });

   if (createdUrl) {
      _cleanupFns.push(() => URL.revokeObjectURL(url));
   }
}

function _cleanup() {
   _cleanupFns.forEach(fn => { try { fn(); } catch (_) {} });
   _cleanupFns = [];
}

function closeVideoPlayer() {
   if (!_modal) return;
   _video.pause();
   _modal.classList.remove('visible');
   _cleanup();
}

/**
 * Extract the first frame from a video blob/data and draw it on a canvas.
 * @param {ArrayBuffer|Blob} data - Video data
 * @param {string} [mimeType='video/mp4'] - MIME type for the blob
 * @param {HTMLCanvasElement} canvas - Target canvas
 * @param {string} [label] - Optional label to draw as overlay
 * @returns {Promise<boolean>} - true if frame was drawn
 */
async function drawFirstFrame(data, mimeType, canvas, label) {
   const ctx = canvas.getContext('2d');
   const W = canvas.width, H = canvas.height;

   // Fill background
   const style = getComputedStyle(document.body);
   const bgColor = style.getPropertyValue('--c0').trim() || '#1a1a1a';
   ctx.fillStyle = bgColor;
   ctx.fillRect(0, 0, W, H);

   try {
      let blob;
      if (data instanceof Blob) {
         blob = data;
      } else {
         blob = new Blob([data], { type: mimeType || 'video/mp4' });
      }
      const url = URL.createObjectURL(blob);

      // Use a temporary video element to grab the first frame
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.src = url;

      await new Promise((resolve, reject) => {
         video.addEventListener('loadeddata', resolve, { once: true });
         video.addEventListener('error', reject, { once: true });
         // Timeout fallback — some formats won't load
         setTimeout(resolve, 3000);
      });

      // Seek to 0 and wait for seeked event
      if (video.readyState >= 2 && video.videoWidth > 0) {
         await new Promise((resolve) => {
            video.currentTime = 0.1;
            video.addEventListener('seeked', resolve, { once: true });
            setTimeout(resolve, 2000);
         });

         // Draw the frame centered on the canvas
         const vw = video.videoWidth, vh = video.videoHeight;
         const scale = Math.min(W / vw, (H - 12) / vh);
         const dw = vw * scale, dh = vh * scale;
         const dx = (W - dw) / 2, dy = (H - dh) / 2 - 4;

         ctx.drawImage(video, dx, dy, dw, dh);

         // Draw play overlay
         ctx.fillStyle = 'rgba(0,0,0,0.4)';
         ctx.beginPath();
         ctx.arc(W / 2, H / 2, 12, 0, Math.PI * 2);
         ctx.fill();
         ctx.fillStyle = 'rgba(255,255,255,0.9)';
         ctx.beginPath();
         ctx.moveTo(W / 2 - 4, H / 2 - 6);
         ctx.lineTo(W / 2 - 4, H / 2 + 6);
         ctx.lineTo(W / 2 + 6, H / 2);
         ctx.closePath();
         ctx.fill();

         // Label
         if (label) {
            ctx.fillStyle = 'rgba(0,0,0,0.7)';
            ctx.fillRect(0, H - 10, W, 10);
            ctx.fillStyle = 'rgba(255,255,255,0.85)';
            ctx.font = 'bold 7px monospace';
            ctx.fillText(label, 2, H - 2);
         }

         URL.revokeObjectURL(url);
         return true;
      }
      URL.revokeObjectURL(url);
   } catch (e) {
      // Fall through to icon
   }
   return false;
}

// ----------------------------------------------------------------------------
// Image viewer modal — similar pattern to video player
// ----------------------------------------------------------------------------

let _imgModal = null;
let _imgCleanupFns = [];

function _ensureImgModal() {
   if (_imgModal) return _imgModal;

   _imgModal = document.createElement('div');
   _imgModal.className = 'ivm-overlay';
   _imgModal.innerHTML = `
      <div class="ivm-container">
         <div class="ivm-header">
            <span class="ivm-title"></span>
            <button class="ivm-close" title="Close (Esc)">✕</button>
         </div>
         <div class="ivm-image-wrap">
            <img class="ivm-image" />
         </div>
      </div>
   `;
   document.body.appendChild(_imgModal);

   // Inject styles (merged with vpm styles, using same id check)
   if (!document.getElementById('ivm-styles')) {
      const style = document.createElement('style');
      style.id = 'ivm-styles';
      style.textContent = `
.ivm-overlay {
   position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
   background: rgba(0,0,0,0.9); z-index: 100001;
   display: none; align-items: center; justify-content: center;
   font-family: monospace;
}
.ivm-overlay.visible { display: flex; }
.ivm-container {
   background: var(--c1, #1a1a1a); border: 1px solid var(--c4, #444);
   border-radius: 4px; max-width: 96vw; max-height: 96vh;
   display: flex; flex-direction: column; overflow: hidden;
   box-shadow: 0 8px 32px rgba(0,0,0,0.6);
}
.ivm-header {
   display: flex; align-items: center; justify-content: space-between;
   padding: 6px 10px; background: var(--c2, #222); border-bottom: 1px solid var(--c4, #444);
   flex-shrink: 0;
}
.ivm-title {
   color: var(--cf, #ccc); font-size: 11px; font-weight: bold;
   overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
   max-width: 400px;
}
.ivm-close {
   background: none; border: none; color: var(--cf, #ccc);
   font-size: 14px; cursor: pointer; padding: 2px 6px; border-radius: 3px;
}
.ivm-close:hover { background: var(--c4, #444); color: #fff; }
.ivm-image-wrap {
   background: #000; display: flex; align-items: center; justify-content: center;
   padding: 4px; overflow: auto;
}
.ivm-image {
   max-width: 94vw; max-height: 88vh; display: block;
   object-fit: contain;
}
`;
      document.head.appendChild(style);
   }

   _imgModal.querySelector('.ivm-close').addEventListener('click', closeImageViewer);
   _imgModal.addEventListener('click', (e) => {
      if (e.target === _imgModal) closeImageViewer();
   });

   return _imgModal;
}

/**
 * Open the image viewer modal.
 * @param {Object} opts
 * @param {string} [opts.url] - URL to view
 * @param {Blob} [opts.blob] - Blob to create URL from
 * @param {string} [opts.name] - Display name
 * @param {boolean} [opts.downloadable=false]
 * @param {string} [opts.downloadUrl]
 * @param {string} [opts.downloadName]
 */
function openImageViewer(opts = {}) {
   _ensureImgModal();

   _imgCleanup();
   const img = _imgModal.querySelector('.ivm-image');
   img.removeAttribute('src');

   let url = opts.url;
   let createdUrl = false;
   if (!url && opts.blob) {
      url = URL.createObjectURL(opts.blob);
      createdUrl = true;
   }
   if (!url) return;

   img.src = url;
   _imgModal.querySelector('.ivm-title').textContent = opts.name || '';

   // Add download button to header if downloadable
   const header = _imgModal.querySelector('.ivm-header');
   let dlBtn = header.querySelector('.ivm-download');
   if (opts.downloadable) {
      if (!dlBtn) {
         dlBtn = document.createElement('button');
         dlBtn.className = 'ivm-close ivm-download';
         dlBtn.textContent = '⬇';
         dlBtn.title = 'Download';
         dlBtn.style.marginRight = '4px';
         header.insertBefore(dlBtn, header.querySelector('.ivm-close'));
      }
      dlBtn.style.display = '';
      dlBtn.onclick = () => {
         const a = document.createElement('a');
         a.href = opts.downloadUrl || url;
         a.download = opts.downloadName || opts.name || 'image';
         a.click();
      };
   } else if (dlBtn) {
      dlBtn.style.display = 'none';
   }

   _imgModal.classList.add('visible');

   const escHandler = (ev) => { if (ev.key === 'Escape') closeImageViewer(); };
   document.addEventListener('keydown', escHandler);
   _imgCleanupFns.push(() => document.removeEventListener('keydown', escHandler));

   if (createdUrl) {
      _imgCleanupFns.push(() => URL.revokeObjectURL(url));
   }
}

function _imgCleanup() {
   _imgCleanupFns.forEach(fn => { try { fn(); } catch (_) {} });
   _imgCleanupFns = [];
}

function closeImageViewer() {
   if (!_imgModal) return;
   _imgModal.classList.remove('visible');
   const img = _imgModal.querySelector('.ivm-image');
   if (img) img.removeAttribute('src');
   _imgCleanup();
}

module.exports = { openVideoPlayer, closeVideoPlayer, drawFirstFrame, openImageViewer, closeImageViewer };
